/**
 * System-notifications API wrapper (v2 — Project Independence, DNKADM-0062;
 * re-points the v1 `/admin/notifications*` to `/v2/admin/notifications*`, handoff
 * §5.20). The `v2_admin_notification` row is the **same shape** as the v1 row plus
 * an additive `lastEmailedAt`, and the inbox gains a **resolve** action alongside
 * acknowledge (v1 resolution was system-set only).
 *
 * dink-api raises tenant-scoped admin alerts (misconfigured services/resources,
 * failed hold releases, …) as a system-of-record tray — one row per problem whose
 * `occurrenceCount`/`lastSeenAt` climb while the problem persists (dedup is
 * server-side, unique on the open `(type, entityRef)`). This UI VIEWS,
 * ACKNOWLEDGES, and (when the contract provides it) RESOLVES; the system may also
 * flip a row to `RESOLVED` on its own when it stops detecting the problem.
 *
 * The endpoints are role-gated OWNER/ADMIN and resolve the tenant server-side from
 * the JWT. As with `members.ts`, the row shape is treated as a fresh,
 * possibly-incomplete SPA contract, so we parse DEFENSIVELY: a missing or
 * wrong-typed field never throws; it degrades to `null`/`0`. Crucially `type` is
 * an **OPEN-ENDED** code (new producers add codes WITHOUT migration) — it is kept
 * as the raw string and rendered GENERICALLY BY SEVERITY at the render boundary
 * (U2); an unrecognized `type`, `severity`, or `status` never hard-fails. Examples
 * you'll see: `HOLD_RELEASE_FAILED`, `SERVICE_HAS_NO_ASSIGNABLE_STAFF`.
 *
 * Observed wire contract (staging 2026-08-04, DNKADM-0243 — this REPLACES the
 * originally-assumed handoff §5.20 shape, which the server does not implement):
 *  - List GET /v2/admin/notifications?status=OPEN|ACKNOWLEDGED|RESOLVED&size=N
 *         → { content: [{ id, type, severity, message, entityRef,
 *             occurrenceCount, firstSeenAt, lastSeenAt, lastEmailedAt, status,
 *             acknowledgedBy, acknowledgedAt, createdAt }],
 *             page, size, totalElements, totalPages }  (each row field may be null/absent)
 *         A **paged envelope** — the rows live under `content`, and `totalElements`
 *         is the honest total behind the page. `{ notifications: [...] }` and a bare
 *         array are still TOLERATED on read (older/other shapes), never required.
 *         `status` is one of OPEN|ACKNOWLEDGED|RESOLVED ONLY: **`all` is REJECTED**
 *         (`400 INVALID_STATUS`) and is deliberately not representable here — an
 *         "everything" scope is composed CLIENT-SIDE from two calls. Omitting
 *         `status` yields the server's default actionable set (OPEN + ACKNOWLEDGED).
 *  - Ack     POST /v2/admin/notifications/{id}/acknowledge → the updated row
 *            (now ACKNOWLEDGED with acknowledgedBy/acknowledgedAt); foreign id → 404.
 *  - Resolve POST /v2/admin/notifications/{id}/resolve → the updated row (now
 *            RESOLVED). Built to the handoff's "acknowledge/resolve" inbox; if the
 *            live contract omits resolve, the UI DEGRADES to acknowledge-only (the
 *            non-2xx surfaces a toast and the action is simply unavailable).
 *
 * Bearer-only `/v2/admin/**` surface (no CSRF, no cookies — handled by `request`),
 * the bounded 401→refresh→retry + the mutating-audit `X-Session-Mode` stamp
 * (DNKADM-0049). Built to the documented contract and MSW-tested; deploy
 * coordinates with the v2 ship.
 */
import { request } from './client';

/** A notification severity, narrowed; an unknown/absent value parses to `null` (neutral). */
export type NotificationSeverity = 'INFO' | 'WARN' | 'ERROR';

/** A notification lifecycle status, narrowed; an unknown/absent value parses to `null`. */
export type NotificationStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';

/**
 * The list filter's wire value. Only the three real statuses are representable —
 * `'all'` is NOT a wire value (the server answers `400 INVALID_STATUS`), so an
 * "everything" scope is composed client-side. Omit it for the actionable default.
 */
export type NotificationStatusFilter = NotificationStatus;

/**
 * One parsed notification row. Any wire field may be null/absent.
 *  - `type` is the raw OPEN-ENDED code, kept verbatim and mapped to icon/label at
 *    render (U2); never narrowed here so a new producer's code degrades gracefully.
 *  - `severity`/`status` are narrowed to the known unions; an unknown value → null.
 */
export interface Notification {
  /** Stable row id (the ack target). Null only on a malformed wire row. */
  id: number | null;
  /** Open-ended problem code (e.g. `BAY_RESOURCE_UNMATCHED`) — raw string, mapped at render. */
  type: string | null;
  /** ERROR/WARN/INFO drives emphasis; an unknown/absent value → null (neutral). */
  severity: NotificationSeverity | null;
  /** Human-readable description (scrubbed via `scrubBrand` at render). */
  message: string | null;
  /** The offending entity label (e.g. "Bay 3"), when present. */
  entityRef: string | null;
  /** Member/contact id when the notification is member-scoped; null when absent. */
  contactId: string | null;
  /** How many times the problem has been seen (persistent issue = one climbing row). */
  occurrenceCount: number;
  /** ISO instant the problem was first seen. */
  firstSeenAt: string | null;
  /** ISO instant the problem was last seen (drives "last … ago"). */
  lastSeenAt: string | null;
  /** ISO instant the row was last emailed (additive v2 field; null when never emailed). */
  lastEmailedAt: string | null;
  /** OPEN (active) · ACKNOWLEDGED (seen, NOT fixed) · RESOLVED (system- or admin-set); unknown → null. */
  status: NotificationStatus | null;
  /** Who acknowledged it (null until acknowledged). */
  acknowledgedBy: string | null;
  /** ISO instant it was acknowledged (null until acknowledged). */
  acknowledgedAt: string | null;
}

// --- Defensive parsing helpers -------------------------------------------------
// Mirror the `members.ts` idiom: degrade-don't-throw on every field.

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number (accepts numeric strings), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

const SEVERITIES: ReadonlySet<string> = new Set<NotificationSeverity>(['INFO', 'WARN', 'ERROR']);
const STATUSES: ReadonlySet<string> = new Set<NotificationStatus>([
  'OPEN',
  'ACKNOWLEDGED',
  'RESOLVED',
]);

/** Narrow a raw severity to the known union; an unknown/absent value → null (neutral). */
function asSeverity(value: unknown): NotificationSeverity | null {
  return typeof value === 'string' && SEVERITIES.has(value)
    ? (value as NotificationSeverity)
    : null;
}

/** Narrow a raw status to the known union; an unknown/absent value → null. */
function asStatus(value: unknown): NotificationStatus | null {
  return typeof value === 'string' && STATUSES.has(value) ? (value as NotificationStatus) : null;
}

/** Parse one row defensively. `type` is kept raw (open-ended); enums narrow-or-null. */
function parseNotification(raw: unknown): Notification {
  const o = isObject(raw) ? raw : {};
  return {
    id: asNumber(o['id']),
    // OPEN-ENDED: keep the code verbatim, mapped to icon/label at render (U2).
    type: asText(o['type']),
    severity: asSeverity(o['severity']),
    message: asText(o['message']),
    entityRef: asText(o['entityRef']),
    contactId: asText(o['contactId']) ?? asText(o['memberContactId']) ?? asText(o['targetContactId']),
    occurrenceCount: asNumber(o['occurrenceCount']) ?? 0,
    firstSeenAt: asText(o['firstSeenAt']),
    lastSeenAt: asText(o['lastSeenAt']),
    lastEmailedAt: asText(o['lastEmailedAt']),
    status: asStatus(o['status']),
    acknowledgedBy: asText(o['acknowledgedBy']),
    acknowledgedAt: asText(o['acknowledgedAt']),
  };
}

/**
 * Locate the rows in the body. The SHIPPED shape is the paged envelope's
 * `content` (checked first); a bare top-level array and the originally-assumed
 * `{ notifications: [...] }` are kept as TOLERATED fallbacks. An unrecognized body
 * degrades to `[]` rather than throwing.
 */
function extractRows(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (!isObject(body)) return [];
  if (Array.isArray(body['content'])) return body['content'] as unknown[];
  if (Array.isArray(body['notifications'])) return body['notifications'] as unknown[];
  return [];
}

/** Parse the located rows defensively; a missing/non-array body yields `[]`. */
function parseNotifications(body: unknown): Notification[] {
  return extractRows(body).map(parseNotification);
}

/**
 * The paged envelope's `totalElements`. NULL when absent or unparseable — never
 * coerced to `0`, which would read as "no notifications" (exactly the DNKADM-0243
 * bug) and would silently suppress the badge and the truncation hint.
 */
function parseTotal(body: unknown): number | null {
  return isObject(body) ? asNumber(body['totalElements']) : null;
}

/** A page of notifications plus the server's total, for an honest badge + truncation hint. */
export interface NotificationPage {
  items: Notification[];
  /** `totalElements` from the paged envelope; null when the server sent no total. */
  total: number | null;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * One bounded page. The popover shows a page and a hint, never an unbounded fetch.
 */
const NOTIFICATIONS_PAGE_SIZE = 100;

/**
 * `GET /v2/admin/notifications?status=…&size=…` → one parsed page (rows + the
 * server's `totalElements`). With no `status` the server returns its default
 * actionable set (OPEN + ACKNOWLEDGED); pass `'OPEN'`, `'ACKNOWLEDGED'`, or
 * `'RESOLVED'` to scope explicitly. Throws `ApiError` on a non-2xx response (the
 * bell's fail-soft consumer catches it).
 */
export async function getNotifications(
  status?: NotificationStatusFilter,
): Promise<NotificationPage> {
  const body = await request<unknown>('GET', '/v2/admin/notifications', {
    // `status` is omitted entirely when undefined (axios drops undefined params)
    // → the server applies its actionable default (OPEN + ACKNOWLEDGED). `all` is
    // REJECTED by the server (400 INVALID_STATUS) and is deliberately not
    // representable here — the All scope is built client-side in the panel.
    query: { status, size: NOTIFICATIONS_PAGE_SIZE },
  });
  return { items: parseNotifications(body), total: parseTotal(body) };
}

/**
 * `POST /v2/admin/notifications/{id}/acknowledge` → the updated row (now
 * ACKNOWLEDGED, with `acknowledgedBy`/`acknowledgedAt` populated). Bearer-only
 * `/v2/admin/**` (no CSRF, no cookies — handled by `request`). Throws a typed
 * `ApiError` on a non-2xx response — notably `404` NOT_FOUND for a foreign/stale id
 * ("not found — refresh"; the U2 caller toasts + refetches the list). Parses the
 * row defensively.
 */
export async function acknowledgeNotification(id: number): Promise<Notification> {
  const body = await request<unknown>(
    'POST',
    `/v2/admin/notifications/${encodeURIComponent(String(id))}/acknowledge`,
  );
  return parseNotification(body);
}

/**
 * `POST /v2/admin/notifications/{id}/resolve` → the updated row (now RESOLVED).
 * The v2 inbox supports an explicit admin resolve alongside acknowledge (handoff
 * §5.20). DEGRADE-GRACEFULLY: if the live contract omits resolve, this throws the
 * typical `404`/`405` `ApiError` (the caller toasts and the action is simply
 * unavailable — never a hard failure). Bearer-only `/v2/admin/**` (no CSRF, no
 * cookies). Parses the row defensively; same error contract as
 * {@link acknowledgeNotification}.
 */
export async function resolveNotification(id: number): Promise<Notification> {
  const body = await request<unknown>(
    'POST',
    `/v2/admin/notifications/${encodeURIComponent(String(id))}/resolve`,
  );
  return parseNotification(body);
}
